import 'isomorphic-fetch';
import React from 'react'
import ReactDOM from 'react-dom';
import axios from 'axios';
import {Provider} from 'react-redux';
import {createStore, applyMiddleware} from 'redux';
import thunkMiddleware from 'redux-thunk'
import {Router, Route, Redirect, hashHistory} from 'react-router';
import {syncHistoryWithStore, routerMiddleware} from 'react-router-redux';
import {middleware as flashMiddleware} from 'redux-flash';
import {persistStore, persistReducer} from 'redux-persist';
import {createHashHistory, useBasename} from 'history';
import {syncTranslationWithStore, loadTranslations, setLocale} from 'react-redux-i18n';
import storage from 'redux-persist/lib/storage';
import {PersistGate} from 'redux-persist/es/integration/react';
import {Provider as AlertProvider} from 'react-alert';
import AlertTemplate from 'react-alert-template-basic';
import asyncComponent from './AsyncComponent';
import reducers from './store/reducers';
import Config from './config';
import {isLogged} from './helpers/Helpers';
import {isLocalHost, updateQueryStringParameter} from '../core/helpers/CoreHelpers';
import translations from "./config/translations";
import ErrorBoundary from "./ErrorBoundary";
import {setLanguages} from "../core/admin/store/actions/language";
import { composeWithDevTools } from 'redux-devtools-extension';

/**
 * SSO type
 */
const isRedirectSso = isLocalHost() || /.*Version.*Safari.*/.test(window.navigator.userAgent);

/**
 * Polyfill
 */
if (!global._babelPolyfill) {
    require('babel-polyfill');
}

const viewportmeta = document.querySelector('meta[name=viewport]');
viewportmeta.setAttribute('content', "initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0");

/**
 * Async components: Frontend Layout components
 */
const FrontLayout = asyncComponent(() => import('./components/Layouts/FrontLayout'));
const LoginLayout = asyncComponent(() => isRedirectSso ? import('./components/Layouts/LocalLoginLayout') : import('./components/Layouts/LoginLayout'));

/**
 * Async components: Frontend
 */
const Home = asyncComponent(() => import('./pages/Home'));
const Page = asyncComponent(() => import('./pages/Page'));
const Preview = asyncComponent(() => import('./pages/Preview'));

/**
 * Settings
 */
const authorizationIsOn = Config.authorization;
const client = document.getElementById('application').getAttribute('data-client');
const initialRoute = document.getElementById('application').getAttribute('data-initial-route');

/**
 * Init
 *
 */
let initialLoad = true;

/**
 * Middleware
 */
const middleware = [
    thunkMiddleware,
    flashMiddleware(),
    routerMiddleware(hashHistory)
];

/**
 * Persist
 */
const persist = persistReducer({
    key: 'root',
    storage: storage,
    blacklist: [
        'language',
        'user',
        'flash',
        'routing',
        'media',
        'i18n'
    ]
}, reducers);

/**
 * Store configuration
 */
const store = createStore(persist, composeWithDevTools(applyMiddleware(...middleware)));

/**
 * Language sync
 */
syncTranslationWithStore(store);
store.dispatch(loadTranslations(translations));
store.dispatch(setLocale(Config.language));
store.dispatch(setLanguages());

const gate = persistStore(store);
const basename = window.location.pathname + window.location.search;
const browserHistory = useBasename(createHashHistory)({
    basename: basename === '/' ? '' : ''
});

const history = syncHistoryWithStore(browserHistory, store);

/**
 * Configure axios
 */
axios.interceptors.response.use((response) => {
    return response;
}, function (error) {
    return Promise.reject(error);
});

/**
 * Log out version
 */
console.log(`${client} version: ` + Config.version);

/**
 * Auth redirected
 */
let isLoggedIn = isLogged();

/**
 * Clear storage
 */
if (isLoggedIn && initialLoad && authorizationIsOn) {
    Config.clearStorage();
}

/**
 * Wait for iframe change status
 */
store.subscribe(() => {
    let previousValue = isLoggedIn;

    isLoggedIn = store.getState().user.isLoggedIn;

    if (!previousValue && isLoggedIn) {
        if (isRedirectSso) {
            if (window.location.hash.includes('?token=')) {
                history.push(updateQueryStringParameter(window.location.hash, 'token', Config.getToken()).replace('#', ''));
            } else {
                history.push(window.location.hash.replace('#', '') + '?token=' + Config.getToken());
            }
        } else {
            if (window.location.hash.includes('?token=')) {
                window.parent.location.replace(updateQueryStringParameter(window.location.href, 'token', Config.getToken()));
            } else {
                window.parent.location.replace(window.location.href + '?token=' + Config.getToken());
            }
        }
    }
});

/**
 * With Frontend layout
 */
const withFrontendLayout = (WrappedComponent, frontendTheme, pageId) => {
    return class WithAuthorization extends React.Component {
        componentDidMount() {
            document.body.classList.add('front-page');

            if (frontendTheme) {
                document.body.classList.add(frontendTheme);
            }
        }

        componentWillMount() {
            const {location: {pathname}} = this.props;

            if (initialLoad && initialRoute) {
                if (initialRoute !== pathname && pathname === '/') {
                    window.location.replace(window.location.href + initialRoute);
                    initialLoad = false;
                }
            }
        }

        render() {
            let props = this.props;

            if (!isLogged() && authorizationIsOn) {
                return (
                    <LoginLayout {...this.props}/>
                );
            } else {
                if (pageId) {
                    props.params.page = pageId;
                }

                if (WrappedComponent) {
                    return (
                        <FrontLayout {...props} frontendTheme={frontendTheme}>
                            <WrappedComponent {...props} frontendTheme={frontendTheme}/>
                        </FrontLayout>
                    );
                } else {
                    return (
                        <FrontLayout {...props} frontendTheme={frontendTheme}/>
                    );
                }
            }
        }
    }
};

const alertOptions = {
    position: 'middle',
    timeout: 99999999999,
    transition: 'fade',
    type: 'success'
};

client &&
ReactDOM.render(
    <ErrorBoundary>
        <AlertProvider template={AlertTemplate} className='alert-dialog' {...alertOptions}>
            <Provider store={store}>
                <PersistGate persistor={gate}>
                    <Router history={history}>
                        <Route
                            path="/"
                            component={withFrontendLayout(Home, client)}
                        />
                        <Route
                            path="/preview"
                            component={withFrontendLayout(Preview, client)}
                        />
                        <Route
                            path="/page/:page"
                            component={withFrontendLayout(Page, client)}
                        />
                        <Route
                            path="/page/:page/:additionalUrlParams"
                            component={withFrontendLayout(Page, client)}
                        />
                        <Redirect from="*" to={initialRoute}/>
                    </Router>
                </PersistGate>
            </Provider>
        </AlertProvider>
    </ErrorBoundary>,
    document.getElementById('application')
);

export {
    basename,
    store
};


// WEBPACK FOOTER //
// ./src/frontend/index.js